import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import axios from "axios";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import AdminTools from "@/components/AdminTools";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { CalendarIcon as Calendar, MapPinIcon as MapPin, CurrencyDollarIcon as CurrencyDollar, UsersIcon as Users, CheckCircleIcon as CheckCircle, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";
import { formatDate, formatApiError, publicFileUrl } from "@/lib/utils-app";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import AddToCalendarMenu from "@/components/calendar/AddToCalendarMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  show: "Show",
  sport: "Sport competition",
  health_clinic: "Health clinic",
  meetup: "Meetup",
  other: "Event",
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [myReg, setMyReg] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [dogId, setDogId] = useState("");
  const [handlerName, setHandlerName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState(null);
  // Admin-only registrants search
  const [registrants, setRegistrants] = useState([]);
  const [q, setQ] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    axios.get(`${API}/events/${id}`).then((r) => setEvent(r.data));
    if (user) {
      api.get(`/events/${id}/my-registration`).then((r) => setMyReg(r.data.registration));
      api.get("/dogs").then((r) => setDogs(r.data));
    }
  }, [id, user]);

  // Debounced load of admin registrants list whenever q changes
  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(() => {
      api
        .get(`/events/${id}/registrations`, { params: q ? { q } : {} })
        .then((r) => setRegistrants(r.data))
        .catch(() => setRegistrants([]));
    }, 250);
    return () => clearTimeout(t);
  }, [id, q, isAdmin, myReg]);

  const onRegister = async () => {
    if (!dogId || !handlerName) {
      toast.error("Pick a dog and enter handler name");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "event_registration",
        origin_url: webOrigin(),
        event_id: id,
        dog_id: dogId,
        handler_name: handlerName,
        ...(promo?.valid ? { promo_code: promo.code } : {}),
      });
      if (data.free) {
        toast.success("You're registered! See you there.");
        setOpen(false);
        // refresh
        const r = await api.get(`/events/${id}/my-registration`);
        setMyReg(r.data.registration);
        return;
      }
      if (data.url) {
        toast.message("Redirecting to secure checkout…");
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!event)
    return (
      <PublicShell>
        <div className="text-[#6B6B6B]">Loading…</div>
      </PublicShell>
    );

  return (
    <PublicShell>
      <SeoHead
        title={`${event.title} · ${formatDate(event.start_date)} · Armstrong K9 Registry`}
        description={(event.description || `${TYPE_LABELS[event.event_type] || "Event"} on ${formatDate(event.start_date)} at ${event.location}. ${event.registration_count} dogs registered.`).slice(0, 220)}
        imageUrl={event.cover_image ? publicFileUrl(event.cover_image) : `${webOrigin()}/logo-512.png`}
        url={`${webOrigin()}/events/${event.id}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": event.title,
          "description": event.description || "",
          "startDate": event.start_date,
          "endDate": event.end_date || event.start_date,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": event.location,
            "address": event.location,
          },
          "image": event.cover_image ? publicFileUrl(event.cover_image) : `${webOrigin()}/logo-512.png`,
          "organizer": {
            "@type": "Organization",
            "name": "Armstrong K9 Registry",
            "url": webOrigin(),
          },
          ...(event.entry_fee >= 0 && {
            "offers": {
              "@type": "Offer",
              "price": event.entry_fee || 0,
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": `${webOrigin()}/events/${event.id}`,
            },
          }),
        }}
        breadcrumbs={[
          { name: "Home", url: `${webOrigin()}/` },
          { name: "Events", url: `${webOrigin()}/events` },
          { name: event.title, url: `${webOrigin()}/events/${event.id}` },
        ]}
      />
      <div className="bg-[#0E0E0E] text-white rounded-xl p-8 md:p-10 mb-8 relative overflow-hidden min-h-[280px]">
        {event.cover_image ? (
          <img
            src={publicFileUrl(event.cover_image)}
            alt={event.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0E0E0E] via-[#1a1a1a] to-[#C1272D]/30 opacity-90" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
        <div className="relative">
          <span className="bg-white/95 text-ink backdrop-blur rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.15em]">
            {TYPE_LABELS[event.event_type] || "Event"}
          </span>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight mt-4 mb-3">{event.title}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
            <span className="flex items-center gap-2"><Calendar size={16} /> {formatDate(event.start_date)}{event.end_date && event.end_date !== event.start_date && ` — ${formatDate(event.end_date)}`}</span>
            <span className="flex items-center gap-2"><MapPin size={16} /> {event.location}</span>
            <span className="flex items-center gap-2"><Users size={16} /> {event.registration_count} registered</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
          <h2 className="font-spectral text-2xl mb-3">About this event</h2>
          <p className="text-ink leading-relaxed whitespace-pre-wrap">
            {event.description || "No description provided."}
          </p>
        </div>
        <aside className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] h-fit md:sticky md:top-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1">Entry fee</p>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="font-spectral text-4xl tracking-tight">
              {event.entry_fee > 0 ? `$${event.entry_fee.toFixed(0)}` : "Free"}
            </span>
            {event.entry_fee > 0 && <span className="text-sm text-[#6B6B6B]">per dog</span>}
          </div>
          <p className="text-xs text-[#6B6B6B] mb-5">Plan subscribers register for free.</p>

          {/* Add to Calendar (Google / Apple / Outlook). Available
              to anonymous visitors too — they shouldn't have to
              register before saving an event to their calendar. */}
          <div className="mb-4 pb-4 border-b border-[#EAEAEA]">
            <AddToCalendarMenu
              title={event.title}
              startsAt={event.start_date}
              endsAt={event.end_date}
              durationMin={60}
              location={event.location || ""}
              description={event.description || ""}
              icsUrl={`/events/${event.id}/ics`}
              icsRequiresAuth={false}
              icsFilename={`${(event.title || "event").replace(/\W+/g, "_").slice(0, 30)}_${event.id.slice(0, 8)}.ics`}
              testId="event-add-to-cal"
              buttonProps={{ className: "rounded-full text-xs w-full" }}
            />
          </div>

          {!user ? (
            <div className="space-y-3">
              <p className="text-sm text-[#6B6B6B]">Sign in to register your dog.</p>
              <Link to="/login" className="block">
                <Button className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full" data-testid="event-signin-cta">
                  Sign in to register
                </Button>
              </Link>
              <Link to="/register" className="block">
                <Button variant="outline" className="w-full rounded-full border-[#E5E5E5]">Create account</Button>
              </Link>
            </div>
          ) : myReg ? (
            <div className="text-center py-2">
              <CheckCircle size={32} weight="duotone" className="text-ink mx-auto mb-2" />
              <p className="font-semibold">You're registered!</p>
              <p className="text-xs text-[#6B6B6B] mt-1">
                {myReg.paid ? "Entry fee paid." : "Covered by your plan."}
              </p>
            </div>
          ) : (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full" data-testid="event-register-btn">
                  {event.entry_fee > 0 ? `Register · $${event.entry_fee.toFixed(0)}` : "Register · Free"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Register for {event.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      Pick a dog *
                    </Label>
                    <Select value={dogId} onValueChange={setDogId}>
                      <SelectTrigger className="mt-1" data-testid="event-dog-select">
                        <SelectValue placeholder="Select a dog" />
                      </SelectTrigger>
                      <SelectContent>
                        {dogs.length === 0 && (
                          <div className="px-3 py-2 text-sm text-[#6B6B6B]">
                            You haven't registered any dogs yet.
                          </div>
                        )}
                        {dogs.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name} ({d.breed})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      Handler name *
                    </Label>
                    <Input
                      value={handlerName}
                      onChange={(e) => setHandlerName(e.target.value)}
                      className="mt-1 rounded-lg"
                      data-testid="event-handler-input"
                    />
                  </div>
                  {event.entry_fee > 0 && (
                    <div>
                      {!isNativeApp() && (
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1 block">
                        Promo code (optional)
                      </Label>
                      )}
                      <CompBanner className="mb-3" />
                      <PromoCodeInput
                        kind="event"
                        amount={event.entry_fee}
                        applied={promo}
                        onApplied={(p) => {
                          setPromo(p);
                          toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
                        }}
                        onCleared={() => setPromo(null)}
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">
                    Cancel
                  </Button>
                  <Button
                    onClick={onRegister}
                    disabled={busy}
                    className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
                    data-testid="event-confirm-btn"
                  >
                    {busy
                      ? "Processing…"
                      : event.entry_fee > 0
                      ? promo?.valid
                        ? `Pay $${promo.final.toFixed(2)} & register`
                        : `Pay $${event.entry_fee.toFixed(0)} & register`
                      : "Confirm registration"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </aside>
      </div>

      {isAdmin && (
        <div
          className="bg-white border border-[#E5E5E5] rounded-xl p-6 md:p-8 mt-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
          data-testid="admin-registrants-panel"
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1">Admin</p>
              <h2 className="font-spectral text-2xl">Registrants</h2>
              <p className="text-sm text-[#6B6B6B] mt-1">
                {registrants.length === 0 && q
                  ? "No matches"
                  : `${registrants.length} entr${registrants.length === 1 ? "y" : "ies"}${q ? ` matching "${q}"` : ""}`}
              </p>
            </div>
            <div className="relative w-full sm:w-80">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
              <Input
                placeholder="Search handler, owner, dog, reg #…"
                className="pl-9 rounded-lg"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="registrants-search-input"
              />
            </div>
          </div>
          {registrants.length === 0 ? (
            <div className="text-sm text-[#6B6B6B] py-6 text-center">
              {q ? "No registrants match your search." : "No registrants yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dog</TableHead>
                    <TableHead>Breed</TableHead>
                    <TableHead>Reg #</TableHead>
                    <TableHead>Handler</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registrants.map((r) => (
                    <TableRow key={r.id} data-testid={`registrant-row-${r.id}`}>
                      <TableCell className="font-medium">{r.dog_name}</TableCell>
                      <TableCell>{r.dog_breed}</TableCell>
                      <TableCell className="font-mono text-xs">{r.registration_number || "—"}</TableCell>
                      <TableCell>{r.handler_name}</TableCell>
                      <TableCell>{r.owner_name}</TableCell>
                      <TableCell className="text-xs">{r.owner_email}</TableCell>
                      <TableCell>
                        <span
                          className={`text-xs font-semibold rounded-full border px-3 py-1 ${
                            r.paid
                              ? "bg-[#0E0E0E]/5 text-[#0E0E0E] border-[#0E0E0E]/20"
                              : "bg-[#C1272D]/10 text-[#C1272D] border-[#C1272D]/20"
                          }`}
                        >
                          {r.paid ? "Paid" : "Plan"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
      {event && (
        <AdminTools
          recordType="event"
          recordId={event.id}
          recordName={event.title || event.id}
          onDeleted={() => navigate("/events")}
        />
      )}
    </PublicShell>
  );
}
